import { useEffect, useState } from "react";

import Navbar from "../components/Navbar";
import api from "../services/api";
import { getSession } from "../utils/session";

export default function DepartmentDashboard() {
  const [schemes, setSchemes] = useState([]);
  const [values, setValues] = useState({});
  const [savedData, setSavedData] = useState([]);

  const [fromMonth, setFromMonth] = useState(
    new Date().toISOString().slice(0, 7)
  );

  const [toMonth, setToMonth] = useState(new Date().toISOString().slice(0, 7));

  const [viewMode, setViewMode] = useState("month");

  const [loadingId, setLoadingId] = useState(null);
  const [message, setMessage] = useState("");

  const [session, setSession] = useState(null);

  /* Load session */

  useEffect(() => {
    const sessionData = getSession();

    if (!sessionData) return;

    setSession(sessionData);

    api
      .get(`/data/schemes/${sessionData.department_id}`)
      .then((res) => setSchemes(res.data));
  }, []);

  /* Load data */

  useEffect(() => {
    if (!session) return;

    loadExistingData();
  }, [fromMonth, toMonth, session, viewMode]);

  /* Load entries */

  const loadExistingData = async () => {
    try {
      let url = "";

      if (viewMode === "month") {
        url = `/data/entries?block=${session.block_id || "null"}&department=${session.department_id}&month=${fromMonth}`;
      } else {
        url = `/data/entries?block=${session.block_id || "null"}&department=${session.department_id}&mode=cumulative`;
      }

      const res = await api.get(url);

      setSavedData(res.data);

      const temp = {};

      res.data.forEach((r) => {
        temp[r.scheme_id] = r.value;
      });

      setValues(temp);
    } catch (err) {
      console.log(err);
    }
  };

  /* Input change */

  const handleChange = (schemeId, value) => {
    setValues({
      ...values,
      [schemeId]: value,
    });
  };

  /* Save entry */

  const saveEntry = async (schemeId) => {
    const value = values[schemeId];

    if (!value) {
      setMessage("Please enter value");
      return;
    }

    try {
      setLoadingId(schemeId);
      setMessage("");

      await api.post("/data/entry", {
        block_id: session.block_id || null,
        scheme_id: Number(schemeId),
        value: Number(value),

        entry_date: fromMonth + "-01",

        created_by: session.user_id,
      });

      setMessage("Saved successfully");

      loadExistingData();
    } catch (err) {
      if (err.response?.data?.error) {
        setMessage(err.response.data.error);
      } else {
        setMessage("Save failed");
      }
    }

    setLoadingId(null);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="max-w-6xl mx-auto p-10">
        <h2 className="text-2xl font-bold mb-2">Department Data Entry</h2>

        {session && (
          <p className="text-gray-600 mb-6">
            Department: <strong>{session.department_name}</strong> | Block:{" "}
            <strong>{session.block_name ? session.block_name : "District Level"}</strong>
          </p>
        )}

        {/* Filters */}

        <div className="flex gap-4 mb-6 flex-wrap">
          <div>
            <label className="text-sm text-gray-600">From Month</label>

            <input
              type="month"
              value={fromMonth}
              onChange={(e) => setFromMonth(e.target.value)}
              className="border p-2 rounded ml-2"
            />
          </div>

          <div>
            <label className="text-sm text-gray-600">To Month</label>

            <input
              type="month"
              value={toMonth}
              onChange={(e) => setToMonth(e.target.value)}
              className="border p-2 rounded ml-2"
            />
          </div>

          <div>
            <label className="text-sm text-gray-600">View Mode</label>

            <select
              value={viewMode}
              onChange={(e) => setViewMode(e.target.value)}
              className="border p-2 rounded ml-2"
            >
              <option value="month">Month Wise</option>

              <option value="cumulative">Cumulative</option>
            </select>
          </div>
        </div>

        {/* Data Entry Table */}

        <div className="bg-white shadow rounded p-6 mb-10">
          <h3 className="text-lg font-semibold mb-4">Enter Monthly Data</h3>

          <table className="min-w-full">
            <thead className="bg-gray-100">
              <tr>
                <th className="p-3 text-left">Scheme</th>

                <th className="p-3 text-left">Value</th>

                <th className="p-3 text-left">Action</th>
              </tr>
            </thead>

            <tbody>
              {schemes.map((s) => {
                const saved = savedData.find((d) => d.scheme_id === s.id);

                return (
                  <tr key={s.id} className="border-t">
                    <td className="p-3">{s.name}</td>

                    <td className="p-3">
                      <input
                        type="number"
                        value={values[s.id] || ""}
                        placeholder="Enter value"
                        className="border p-2 rounded w-40"
                        onChange={(e) => handleChange(s.id, e.target.value)}
                      />
                    </td>

                    <td className="p-3">
                      <button
                        onClick={() => saveEntry(s.id)}
                        disabled={loadingId === s.id}
                        className="bg-blue-600 text-white px-4 py-2 rounded"
                      >
                        {loadingId === s.id
                          ? "Saving..."
                          : saved
                          ? "Update"
                          : "Save"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {message && <p className="mt-4 text-gray-600">{message}</p>}
        </div>

        {/* Data History */}

        <div className="bg-white shadow rounded p-6">
          <h3 className="text-lg font-semibold mb-4">Submitted Data</h3>

          <table className="min-w-full">
            <thead className="bg-gray-100">
              <tr>
                <th className="p-3 text-left">Scheme</th>

                <th className="p-3 text-left">Month</th>

                <th className="p-3 text-left">Value</th>
              </tr>
            </thead>

            <tbody>
  {savedData.map((row, i) => {
    const scheme = schemes.find(s => s.id === row.scheme_id);

    return (
      <tr key={i} className="border-t">
        <td className="p-3">
          {scheme?.name || "-"}
        </td>

        <td className="p-3">
          {viewMode === "cumulative"
            ? "Cumulative"
            : row.entry_date
            ? new Date(row.entry_date).toLocaleDateString("en-IN", {
                month: "short",
                year: "numeric",
              })
            : "-"}
        </td>

        <td className="p-3 font-semibold text-blue-700">
          {row.value}
        </td>
      </tr>
    );
  })}
</tbody>

          </table>
        </div>
      </div>
    </div>
  );
}
